import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import hive from '@bombas/hive';
import * as Yup from 'yup';
import * as Styled from './CouponDialogSmsForm.styled';
import { post } from '../../../../util/request';
import { platform_base_url } from '../../../../util/config';
import { formatPhone } from '../../../../util/util';
import RichTextPropTypes from '../../../../prop-types/rich-text';

const SMSSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^\(?\d{3}\)\s\d{3}-\d{4}$/, 'Please enter a valid number.')
    .required('Please enter a valid number.')
});

/**
 * Coupon Dialog SMS Form allows a user to sign up for Bombas SMS notifications.
 */
const CouponDialogSMSForm = ({
  email,
  onComplete,
  smsTermsOfService,
  emailAlreadyExists,
  bodyHeadingText
}) => {
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    hive.track('COUPON_MODAL_SMS_STEP_VIEW');
  }, []);

  const handleSubmit = ({ phone }, { setErrors }) => {
    setIsFetching(true);

    post(`${platform_base_url}/v1/marketing/sms-sign-up`, {
      contact: {
        phone: phone.replace(/[^\d]/g, ''),
        email
      }
    })
      .then(() => {
        hive.track('COUPON_MODAL_SMS_SUCCESS', {
          customer: {
            phone: phone.replace(/[^\d]/g, ''),
            returning: !!emailAlreadyExists
          }
        });
        onComplete();
      })
      .catch((error) => {
        setIsFetching(false);
        hive.track('COUPON_MODAL_SMS_FAIL');

        setErrors({
          phone:
            error?.response?.message ||
            'Unable to contact server. Please try again.'
        });
      });
  };

  return (
    <Styled.CouponDialogSmsForm>
      <Styled.ContentContainer>
        {bodyHeadingText && (
          <Styled.BodyHeading>{bodyHeadingText}</Styled.BodyHeading>
        )}
        <Styled.ContentfulTextBlock
          content={smsTermsOfService}
          paddingTop="0px"
          paddingRight="0px"
          paddingBottom="0px"
          paddingLeft="0px"
          backgroundColor="grey40"
        />
        <Formik
          initialValues={{ phone: '' }}
          validationSchema={SMSSchema}
          onSubmit={handleSubmit}
        >
          <Styled.SmsForm>
            <Styled.TextInput
              id="couponFormSmsPhone"
              name="phone"
              type="phone"
              label="Mobile Number"
              onChange={(e) => {
                e.target.value = formatPhone(e.target.value);
              }}
            />
            <Styled.Button
              type="submit"
              size="large"
              variant="primary"
              isLoading={isFetching}
            >
              Sign Up For Text
            </Styled.Button>
          </Styled.SmsForm>
        </Formik>
      </Styled.ContentContainer>
    </Styled.CouponDialogSmsForm>
  );
};

CouponDialogSMSForm.propTypes = {
  email: PropTypes.string.isRequired,
  /**
   * A function that is called upon successful mailing list signup.
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * Contentful object, Settings->RichTextResource for SMS Legal Copy
   */
  smsTermsOfService: PropTypes.shape(RichTextPropTypes),
  /**
   * A heading to render above the body copy
   */
  bodyHeadingText: PropTypes.string,
  /**
   * 'true' if email has a past order associated to it
   */
  emailAlreadyExists: PropTypes.bool
};

CouponDialogSMSForm.defaultProps = {
  bodyHeadingText: '',
  emailAlreadyExists: false
};

export default CouponDialogSMSForm;
